import React, { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Close as CloseIcon, CloudUpload as UploadIcon, Image as ImageIcon, VideoFile as VideoIcon, Description as DocumentIcon } from '@mui/icons-material'
import { catalogApi } from '../../services/catalogApi'
import { ContentItem } from '../../types/catalog'
import { validateFile } from '../../utils/uploads'
import { 
  ALLOWED_IMAGE_TYPES, 
  ALLOWED_VIDEO_TYPES, 
  ALLOWED_DOCUMENT_TYPES 
} from '../../constants/uploads'
import { extractErrorMessage } from '../../utils/errorUtils'

interface AddContentDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  smbId: string
  itemToEdit?: ContentItem | null
  initialType?: string
}

type ContentType = 'image' | 'video' | 'document' | 'external_video' | null | undefined

const AddContentDialog: React.FC<AddContentDialogProps> = ({ open, onClose, onSuccess, smbId, itemToEdit, initialType }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [contentType, setContentType] = useState<ContentType>('image')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [externalUrl, setExternalUrl] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const [urlValidationError, setUrlValidationError] = useState<string | null>(null)

  const [status, setStatus] = useState<'active' | 'inactive'>('active')
  const [removeMedia, setRemoveMedia] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const isEdit = Boolean(itemToEdit)
  const isExternalVideoEdit = isEdit && itemToEdit?.type === 'external_video'
  const isIntegrationProduct = isEdit && !!itemToEdit?.has_integration && itemToEdit?.type !== 'external_video'

  React.useEffect(() => {
    if (itemToEdit) {
      setTitle(itemToEdit.title || '')
      setDescription(itemToEdit.description || '')
      setContentType(itemToEdit.type)
      setTagsInput(itemToEdit.tags?.join(', ') || '')
      setStatus((itemToEdit.status as 'active' | 'inactive') || 'active')
      // Prefill external URL when editing an item that has a file URL
      setExternalUrl(itemToEdit.file_url || '')
    } else {
      // Reset for new item
      setTitle('')
      setDescription('')
      
      // Set to initial type if provided and valid, otherwise default to 'image'
      const initialContentType = (initialType === 'image' || initialType === 'video' || initialType === 'document') 
        ? initialType 
        : 'image'
      setContentType(initialContentType)
      
      setTagsInput('')
      setStatus('active')
      setExternalUrl('')
      setFile(null)
      setRemoveMedia(false)
    }
  }, [itemToEdit, open, initialType])
  const handleContentTypeChange = (_: React.MouseEvent<HTMLElement>, nextType: ContentType | null) => {
    if (nextType) {
      setContentType(nextType)
      // Only clear the selected file — preserve any entered external URL and text fields
      setFile(null)
      setError(null)
      setUrlValidationError(null)
      setShowFieldErrors(false)
      
      // If in edit mode and changing type, auto-flag existing media for removal
      if (isEdit && itemToEdit && itemToEdit.media_id && nextType !== itemToEdit.type) {
        setRemoveMedia(true)
      } else if (isEdit && itemToEdit && nextType === itemToEdit.type) {
        // Restoring original type, un-flag removal if it was auto-flagged
        setRemoveMedia(false)
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      // files should only be selected for valid upload types
      if (!contentType || contentType === 'external_video') {
        setError('File uploads are not supported for this content type')
        return
      }
      
      const validationError = validateFile(selectedFile, contentType)
      if (validationError) {
        setError(validationError)
        // Reset file input
        if (e.target) e.target.value = ''
        return
      }

      setFile(selectedFile)
      setError(null)
      if (!title) {
        // Auto-fill title with filename (without extension)
        const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "")
        setTitle(nameWithoutExt)
      }
    }
    // Reset file input value to allow selecting the same file again
    if (e.target) {
      e.target.value = ''
    }
  }

  const hasChanges = isEdit && itemToEdit ? (
    title !== (itemToEdit.title || '') ||
    description !== (itemToEdit.description || '') ||
    tagsInput !== (itemToEdit.tags?.join(', ') || '') ||
    status !== (itemToEdit.status || 'active') ||
    contentType !== itemToEdit.type ||
    file !== null ||
    removeMedia ||
    (externalUrl !== '' && externalUrl !== itemToEdit.file_url)
  ) : false

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setUploading(true)
    setError(null)
    setUrlValidationError(null)

    // Validation
    const errors: string[] = []
    if (!title.trim()) errors.push('Title')
    if (!description.trim()) errors.push('Description')

    if (errors.length > 0) {
      setError(`The following fields are required: ${errors.join(', ')}`)
      setShowFieldErrors(true)
      setUploading(false)
      return
    }

      if (file && externalUrl) {
        setError('Please provide either a file or an external URL, not both.')
        setUploading(false)
        return
      }
      
      const isMissingMedia = (!isEdit && !file && !externalUrl) || (isEdit && removeMedia && !file && !externalUrl)
      if (isMissingMedia) {
        setError('Please provide a file or an external URL')
        setUploading(false)
        return
      }

      if (externalUrl) {
        try {
          new URL(externalUrl)

          // Basic type validation for URL
          const urlLower = externalUrl.toLowerCase()
          if (contentType === 'image') {
            const isImage = /\.(jpg|jpeg|png)$/.test(urlLower) || urlLower.includes('drive.google')
            if (!isImage) {
              setUrlValidationError('Please provide a valid image URL (.JPG, .JPEG, .PNG, or Google Drive link)')
              setUploading(false)
              return
            }
          } else if (contentType === 'video') {
            const isVideo = /\.(mp4|3gp|3gpp)$/.test(urlLower) ||
              urlLower.includes('youtube.com') ||
              urlLower.includes('youtu.be')
            if (!isVideo) {
              setUrlValidationError('Please provide a valid video URL (MP4, 3GP, or YouTube link)')
              setUploading(false)
              return
            }
          } else if (contentType === 'document') {
            const isDoc = /\.(pdf|doc|docx)$/.test(urlLower) || urlLower.includes('document') || urlLower.includes('drive.google')
            if (!isDoc) {
              setUrlValidationError('Please provide a valid PDF or Word document URL or Google Drive link')
              setUploading(false)
              return
            }
          }
        } catch (e) {
          setUrlValidationError('Please enter a valid URL (starting with http:// or https://)')
          setUploading(false)
          return
        }
      }

    try {
      if (isEdit && itemToEdit) {
        const formData = new FormData()
        
        const cleanTags = tagsInput.split(',').map(t => t.trim()).filter(t => t)
        const originalTags = itemToEdit.tags || []
        const tagsChanged = cleanTags.length !== originalTags.length || !cleanTags.every((t, i) => t === originalTags[i])

        if (title !== itemToEdit.title) formData.append('title', title)
        if (description !== itemToEdit.description) formData.append('description', description)
        if (tagsChanged) formData.append('tags', cleanTags.join(','))
        if (status !== (itemToEdit.status || 'active')) formData.append('status', status)
        if (contentType !== itemToEdit.type && contentType) formData.append('type', contentType)
        formData.append('remove_media', String(removeMedia))
        if (file) formData.append('file', file)
        if (!file && externalUrl) formData.append('file_url', externalUrl)

        if (itemToEdit.has_integration) {
          // Keep JSON payload for SMBProducts since they don't support multipart yet
          const updatePayload: any = {}
          if (title !== itemToEdit.title) updatePayload.title = title
          if (description !== itemToEdit.description) updatePayload.description = description
          if (tagsChanged) updatePayload.tags = cleanTags
          await catalogApi.updateSMBProduct(itemToEdit.id, smbId, updatePayload)
        } else {
          await catalogApi.updateContent(itemToEdit.id, smbId, formData)
        }
      } else {
        // Handle Create (Multipart Form Data)
        const formData = new FormData()
        formData.append('type', contentType || 'image')
        const detectedSourceType = file ? 'upload' : 'external'
        formData.append('source_type', detectedSourceType)
        formData.append('title', title)
        formData.append('description', description)
        formData.append('status', status)
        formData.append('tags', tagsInput.split(',').map(t => t.trim()).filter(t => t).join(','))

        if (file) {
          formData.append('file', file)
        } else if (externalUrl) {
          formData.append('file_url', externalUrl)
        }

        await catalogApi.createContent(smbId, formData)
      }
      handleClose()
      onSuccess()
    } catch (err: any) {
      if (err.response?.status === 422) {
        setError('Something went wrong. Please refresh.')
      } else if (err.response?.status === 404) {
        setError('Content not found')
      } else {
        setError(extractErrorMessage(err, `Failed to ${isEdit ? 'update' : 'create'} content`))
      }
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    if (!uploading) {

      setContentType('image')
      setTitle('')
      setDescription('')
      setTagsInput('')
      setStatus('active')
      setExternalUrl('')
      setFile(null)
      setRemoveMedia(false)
      setError(null)
      setShowFieldErrors(false)

      // Reset file input value
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      onClose()
    }
  }

  const acceptTypes: Record<string, string> = {
    image: ALLOWED_IMAGE_TYPES.join(', '),
    video: ALLOWED_VIDEO_TYPES.join(', '),
    document: ALLOWED_DOCUMENT_TYPES.join(', '),
    external_video: '',
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography variant="h5" fontWeight="700">{isEdit ? 'Edit Content' : 'Add New Content'}</Typography>
          <IconButton onClick={handleClose} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1, px: 3 }}>

          {!isIntegrationProduct && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content Type
            </Typography>
            <ToggleButtonGroup
              value={contentType}
              exclusive
              onChange={handleContentTypeChange}
              fullWidth
              color="primary"
              size="small"
              disabled={isEdit && itemToEdit?.has_integration}
            >
              <ToggleButton value="image">
                <ImageIcon sx={{ mr: 1, fontSize: 20 }} /> Image
              </ToggleButton>
              <ToggleButton value="video">
                <VideoIcon sx={{ mr: 1, fontSize: 20 }} /> Video
              </ToggleButton>
              <ToggleButton value="document">
                <DocumentIcon sx={{ mr: 1, fontSize: 20 }} /> Doc
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          )}

          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            error={showFieldErrors && !title.trim()}
            helperText={showFieldErrors && !title.trim() ? "Title is required" : ""}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            error={showFieldErrors && !description.trim()}
            helperText={showFieldErrors && !description.trim() ? "Description is required" : ""}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Tags (Comma separated)"
            placeholder="social, promo, 2024"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            helperText="Add tags to help you organize your content (optional)"
            sx={{ mb: 1 }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 4 }}>
            {tagsInput.split(',').map(t => t.trim()).filter(t => t).map((tag, idx) => (
              <Chip key={idx} label={tag} size="small" variant="outlined" />
            ))}
          </Box>


          {/* Status Field */}
          <Box sx={{ mb: 3, mt: -2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <ToggleButtonGroup 
              value={status} 
              exclusive 
              onChange={(_, v) => v && setStatus(v)} 
              fullWidth
              size="small" 
              color="primary"
              disabled={isEdit && itemToEdit?.has_integration} // Integration products manage their own status
            >
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="inactive">Inactive</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {!isIntegrationProduct && (
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            {isEdit ? 'Replace Media (optional)' : 'Upload Content'}
          </Typography>
          )}

          {isEdit && itemToEdit?.media_id && !file && !removeMedia && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" color={itemToEdit.has_integration ? "textSecondary" : "primary"} fontWeight="600" sx={{ wordBreak: 'break-all' }}>
                  Uploaded Media
                </Typography>
                {itemToEdit.file_url && (
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, wordBreak: 'break-all' }}>
                    URL: {itemToEdit.file_url}
                  </Typography>
                )}
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={() => setRemoveMedia(true)}
                  disabled={itemToEdit.has_integration}
                  sx={{ mt: 1, borderRadius: 2 }}
                >
                  Remove media
                </Button>
              </Box>
            </Paper>
          )}

          {( isExternalVideoEdit || (( !isEdit || removeMedia || (!itemToEdit?.media_id && !itemToEdit?.file_url) || (isEdit && itemToEdit?.file_url) ) && !itemToEdit?.has_integration) ) && (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: isExternalVideoEdit ? 'not-allowed' : 'pointer',
                  borderStyle: 'dashed',
                  borderColor: file ? 'primary.main' : 'divider',
                  bgcolor: 'action.hover',
                  mb: 2,
                  transition: 'all 0.2s',
                  opacity: isExternalVideoEdit ? 0.65 : 1,
                  pointerEvents: isExternalVideoEdit ? 'none' : 'auto',
                  ...(!isExternalVideoEdit && { '&:hover': { borderColor: 'primary.main', bgcolor: 'action.selected' } })
                }}
                onClick={() => !isExternalVideoEdit && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept={contentType ? acceptTypes[contentType] : undefined}
                  onChange={handleFileSelect}
                />
                {file ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1" color="primary" fontWeight="600" sx={{ wordBreak: 'break-all' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      sx={{ borderRadius: 2 }}
                    >
                      Remove file
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography>Click to select or drag and drop</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Supported: {
                        contentType === 'image' ? 'JPEG/JPG, PNG' :
                          contentType === 'video' ? 'MP4, 3GP' :
                            'PDF, DOC, DOCX'
                      }
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="textSecondary">OR USE URL</Typography>
              </Divider>

              <TextField
                fullWidth
                label="Content URL"
                placeholder="https://example.com/item"
                value={externalUrl}
                onChange={(e) => {
                  setExternalUrl(e.target.value)
                  setUrlValidationError(null)
                }}
                disabled={isExternalVideoEdit}
                error={Boolean((file && externalUrl) || urlValidationError)}
                sx={{ mb: 1 }}
              />

              {((file && externalUrl) || urlValidationError) && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    file && externalUrl ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button color="inherit" size="small" onClick={() => setFile(null)}>CLEAR FILE</Button>
                        <Button color="inherit" size="small" onClick={() => setExternalUrl('')}>CLEAR URL</Button>
                      </Box>
                    ) : null
                  }
                >
                  {urlValidationError || "Both file and URL provided. Please choose one to proceed."}
                </Alert>
              )}
            </>
          )}

          {error && <Alert severity="error" sx={{ mt: 3, mb: 1 }}>{error}</Alert>}
        </DialogContent>

        {uploading && <LinearProgress />}

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={uploading || (isEdit && !hasChanges)}
          >
            {uploading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Add Content')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AddContentDialog
